// src/pages/materials/MaterialBoard.jsx
import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../../api/axios";
import BoardToolbar from "../../components/common/board/BoardToolbar";
import BoardTable from "../../components/common/board/BoardTable";
import BoardPagination from "../../components/common/board/BoardPagination";
import SearchBar from "../../components/common/board/SearchFilterBar";
import { fetchUserDetail } from "../../api/users/user";
import { formatDate } from "../../utils/date";
import PageHeader from "../../components/common/ui/PageHeader";

const LABELS = {
  PS: "자재승인요청",
  PO: "발주",
  GR: "입고",
  GI: "출고",
  QC: "검수",
  CK: "확인",
};

const ALL_TYPES = ["PS", "PO", "GR", "GI", "QC", "CK"];

const CONTEXT_BY_FORM = {
  PS: "plan",
  PO: "order",
  GR: "in",
  GI: "out",
  QC: "request",
  CK: "confirm",
};

const statusColor = (status) => {
  switch (status) {
    case "submit":
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "approved":
    case "done":
      return "bg-green-100 text-green-800";
    case "rejected":
    case "canceled":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const statusText = (s) =>
({
  none: "초안",
  draft: "초안",
  submit: "상신",
  pending: "진행중",
  approved: "승인",
  done: "완료",
  rejected: "반려",
  cancel: "취소",
  canceled: "취소",
}[s] ?? "-");

const parseMaybeJSON = (v) => {
  if (!v) return null;
  if (typeof v === "string") {
    try {
      return JSON.parse(v);
    } catch {
      return null;
    }
  }
  return typeof v === "object" ? v : null;
};

const joinKoreanName = (l, f) => {
  l = l?.trim() || "";
  f = f?.trim() || "";
  return l && f ? `${l}${f}` : f || l || "";
};

const formatNameOnly = (u) => {
  if (!u) return "-";
  if (typeof u === "string") return u.trim() || "-";
  if (typeof u === "number") return `#${u}`;
  if (typeof u === "object")
    return (
      u.name ||
      joinKoreanName(u.last_name, u.first_name) ||
      u.username ||
      u.email ||
      "-"
    );
  return "-";
};

const getTitle = (row) => {
  const direct =
    row?.title ||
    row?.name ||
    row?.subject ||
    row?.document_title ||
    row?.doc_title;
  if (typeof direct === "string" && direct.trim()) return direct.trim();

  const hdr = parseMaybeJSON(row?.header);
  const hTitle = hdr?.title || hdr?.values?.title || hdr?.meta?.title || null;
  if (typeof hTitle === "string" && hTitle.trim()) return hTitle.trim();

  const mmHdr = parseMaybeJSON(row?.material_meta?.header);
  const mmTitle =
    mmHdr?.title || mmHdr?.values?.title || mmHdr?.meta?.title || null;
  if (typeof mmTitle === "string" && mmTitle.trim()) return mmTitle.trim();

  const doc = row?.document || row?.content || {};
  const nested =
    doc?.title ||
    doc?.name ||
    doc?.subject ||
    doc?.values?.title ||
    doc?.meta?.title;
  if (typeof nested === "string" && nested.trim()) return nested.trim();

  const extra = parseMaybeJSON(row?.extra);
  const body =
    parseMaybeJSON(extra?.content) ||
    extra?.content ||
    parseMaybeJSON(row?.template_snapshot) ||
    {};
  const exTitle =
    body?.title || body?.name || body?.subject || body?.values?.title;
  if (typeof exTitle === "string" && exTitle.trim()) return exTitle.trim();

  if (typeof row?.ref_no === "string" && row.ref_no.trim())
    return row.ref_no.trim();

  return "제목 없음";
};

const getFormCode = (row) => {
  const raw =
    row?.form_type ?? row?.doc_type ?? row?.form ?? row?.type ?? row?.category;
  if (!raw) return null;
  const code = String(raw).trim().toUpperCase();
  if (ALL_TYPES.includes(code)) return code;

  const byLabel = Object.entries(LABELS).find(([, label]) => label === code);
  if (byLabel) return byLabel[0];

  const match = code.match(/\b(PS|PO|GR|GI|QC|CK)\b/);
  return match ? match[1] : null;
};

export default function MaterialBoard() {
  const { id: projectId, formType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const normalizedFormType = (formType || "all").toUpperCase();

  const loadRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // 문서 유형 필터 제거 → 내부는 ALL_TYPES 로 유지
  const [types] = useState(ALL_TYPES);

  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title");
  const [ordering] = useState("-created_at");

  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [searchTrigger, setSearchTrigger] = useState(0);
  const [userCache, setUserCache] = useState({});
  const [deletingDns, setDeletingDns] = useState(null);


  const effectiveFormTypes =
    normalizedFormType === "ALL"
      ? ALL_TYPES
      : [normalizedFormType];

  const formTypeCsv = effectiveFormTypes.join(",");

  console.log(formTypeCsv)


  useEffect(() => {
    setPage(1);
  }, [normalizedFormType]);

  const applySearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  const renderWriter = (row) => {
    const txt =
      row?.writer_name ||
      row?.author_name ||
      row?.owner_name ||
      row?.created_by_name;
    if (txt?.trim()) return txt.trim();

    if (Array.isArray(row?.writer) && row.writer.length)
      return formatNameOnly(row.writer[0]);

    const cb = row?.created_by;
    if (cb && typeof cb === "object") return formatNameOnly(cb);
    if (typeof cb === "string" && cb.trim()) return cb.trim();

    const id =
      row?.created_by || row?.created_by_id || row?.created_by?.id || null;

    return id && userCache[id] ? formatNameOnly(userCache[id]) : "-";
  };

  // ---------------------- 데이터 로딩 ---------------------
  useEffect(() => {
    const load = async () => {
      const idx = ++loadRef.current;

      try {
        setLoading(true);
        setError("");

        const params = {
          project: projectId,
          page,
          page_size: pageSize,
          ordering,
          form_type: formTypeCsv,
          q: q.trim() || undefined,
          search_field: searchField,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
        };

        console.log('[MaterialBoard] API 호출:', 'material/docs/', params);
        const res = await api.get("material/docs/", { params });
        console.log('[MaterialBoard] API 응답:', res.data);

        const items = res.data?.results || res.data || [];
        console.log('[MaterialBoard] 파싱된 items:', items.length, '건');
        setRows(items);
        setCount(res.data?.count ?? items.length);

        const ids = Array.from(
          new Set(
            (items || [])
              .map(
                (r) => r?.created_by || r?.created_by_id || r?.created_by?.id
              )
              .filter(Boolean)
          )
        );

        const miss = ids.filter((id) => !userCache[id]);
        if (miss.length) {
          const fetched = await Promise.all(
            miss.map(async (id) => {
              try {
                const u = await fetchUserDetail(id);
                return [id, u?.data || u];
              } catch {
                return [id, null];
              }
            })
          );
          setUserCache((p) => ({ ...p, ...Object.fromEntries(fetched) }));
        }
      } catch (e) {
        console.error('[MaterialBoard] API 에러:', e);
        console.error('[MaterialBoard] 에러 상세:', e.response?.data);
        setError("목록을 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [
    page,
    pageSize,
    formTypeCsv,
    q,
    searchField,
    startDate,
    endDate,
    searchTrigger,
    projectId,
    location.pathname,
  ]);

  const goView = (row) => {
    const code = getFormCode(row);
    const ctx = code ? code : null;
    const dns = row.doc_dns || row.dns;
    if (!ctx || !dns) return;
    navigate(`/project/${projectId}/material/${ctx}/${dns}/view`);
  };

  const handleDelete = async (row) => {
    const dns = row.doc_dns || row.dns;
    if (!dns) return;
    if (!window.confirm("이 문서를 삭제하시겠어요?")) return;

    try {
      setDeletingDns(dns);
      await api.delete(`material/docs/${dns}/`, {
        params: { project: projectId },
      });
      setRows((prev) => prev.filter((r) => (r.doc_dns || r.dns) !== dns));
      setCount((c) => c - 1);
    } catch {
      alert("삭제 실패");
    } finally {
      setDeletingDns(null);
    }
  };

  const currentTitle =
    normalizedFormType === "ALL"
      ? "자재 문서 목록"
      : ALL_TYPES.includes(normalizedFormType)
        ? `${LABELS[normalizedFormType]} 문서 목록`
        : "자재 문서";

  const columns = [
    {
      key: "title",
      header: "제목",
      render: (r) => (
        <div className="truncate max-w-[520px]">{getTitle(r)}</div>
      ),
    },
    {
      key: "form_type",
      header: "문서 유형",
      render: (row) => {
        const cd = getFormCode(row);
        return (
          <span className="rounded-md border px-2 py-[2px] text-xs">
            {cd ? `${LABELS[cd]}` : "-"}
          </span>
        );
      },
    },
    {
      key: "status",
      header: "상태",
      render: (r) => (
        <span
          className={`px-2 py-0.5 text-xs rounded-full ${statusColor(
            r.status
          )}`}
        >
          {statusText(r.status)}
        </span>
      ),
    },
    {
      key: "writer",
      header: "작성자",
      render: renderWriter,
    },
    {
      key: "created_at",
      header: "작성일",
      render: (r) => formatDate(r.created_at),
    },
    {
      key: "actions",
      header: "관리",
      align: "center",
      headerAlign: "center",
      render: (row) => (
        <button
          className="border rounded-lg px-2 py-1 text-xs hover:bg-gray-50"
          disabled={deletingDns === (row.doc_dns || row.dns)}
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(row);
          }}
        >
          {deletingDns === (row.doc_dns || row.dns) ? "삭제중…" : "삭제"}
        </button>
      ),
    },
  ];

  return (
    <div className="p-6">
      <PageHeader>{currentTitle}</PageHeader>

      <BoardToolbar
        left={null}
        right={
          <div className="flex items-center gap-3">
            <SearchBar
              q={q}
              onChangeQ={setQ}
              onSearch={applySearch}
              searchField={searchField}
              onChangeSearchField={setSearchField}
              dateFilter={dateFilter}
              onChangeDateFilter={setDateFilter}
              startDate={startDate}
              endDate={endDate}
              onDateChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
              pageSize={pageSize}
              onChangePageSize={setPageSize}
            />
          </div>
        }
      />

      <BoardTable
        columns={columns}
        rows={rows}
        loading={loading}
        error={error}
        keyField={(r) => r.doc_dns || r.dns}
        onRowClick={goView}
      />

      <BoardPagination
        page={page}
        pageSize={pageSize}
        total={count}
        onPageChange={setPage}
      />
    </div>
  );
}
